import { useCurrentBilikPerson } from 'hooks/use-current-bilik-person/use-current-bilik-person';
import { ProofDocumentButton } from 'modules/review/components/ProofDocumentButton';
import { PublishReviewButton } from 'modules/review/components/PublishReviewButton';
import { SpamReviewButton } from 'modules/review/components/SpamReviewButton/SpamReviewButton';
import ToggleLockReviewButton from 'modules/review/components/ToggleLockReviewButton';
import { isProofStatus } from 'modules/review/types/proof.type';
import React, { FunctionComponent } from 'react';
import { Button, Icon, Popup } from 'semantic-ui-react';
import { NumberParam, useQueryParam } from 'use-query-params';
import { useReviewModals } from '../hooks/useReviewModals';
import { Review } from '../types/review.type';
import { CopyReviewLinkButton } from './CopyReviewLinkButton';
import { EditReviewButton } from './EditReviewButton';
import { ReviewActionsModal } from './ReviewActionsModal';
import { ViewReviewOnlineButton } from './ViewReviewOnlineButton';
import { hasOnlyArchivedViews } from '../utils/hasOnlyArchivedViews';
import { ReviewPresentationsButton } from './ReviewPresentationsButton';

type ReviewActionsProps = {
  review: Review;
  onReviewChange: () => void;
};

export const ReviewActions: FunctionComponent<ReviewActionsProps> = ({
  review,
  onReviewChange,
}) => {
  const [page] = useQueryParam('page', NumberParam);
  const { currentBilikPerson } = useCurrentBilikPerson();
  const reviewHasOnlyArchivedViews = hasOnlyArchivedViews(review);
  const isLockedOrArchived =
    (!!review.locker && review.locker.id !== currentBilikPerson?.id) ||
    reviewHasOnlyArchivedViews;

  const isNotSpam = review.isSpam === false;
  const canPublish = isNotSpam && review.status !== 'ok';
  const canRefuse = isNotSpam && review.status !== 'ko';

  const canSendCheckReminder =
    isNotSpam && review.status !== 'ok' && !!review.checker;

  const canRequestCheck =
    isNotSpam &&
    review.status === 'draft' &&
    review.source === 'BILIK' &&
    !review.checker;

  const canToggleSpam = !review.checker;
  const canEdit = isNotSpam;
  const canManageProof = isNotSpam && review.status !== 'ko';
  const isPublishedOnline = isNotSpam && review.status === 'ok';
  const canToggleLock = review.status === 'draft';

  const {
    modalMetadata,
    openCheckModal,
    openRefuseModal,
    openSmsModal,
    closeModal,
  } = useReviewModals();

  return (
    <>
      <Popup
        content={
          reviewHasOnlyArchivedViews ? 'Fiche archivée' : 'Avis verrouillé'
        }
        disabled={!isLockedOrArchived}
        position="top center"
        trigger={
          <span>
            {canPublish ? (
              <PublishReviewButton
                review={review}
                onReviewChange={onReviewChange}
                disabled={isLockedOrArchived}
              />
            ) : null}
            {canRefuse ? (
              <Button
                basic
                negative
                onClick={(): void => openRefuseModal(review)}
                disabled={isLockedOrArchived}
              >
                <Icon name="remove" />
                Refuser
              </Button>
            ) : null}
            {canSendCheckReminder ? (
              <Button
                basic
                color="grey"
                onClick={(): void => openSmsModal(review)}
                disabled={isLockedOrArchived}
              >
                <Icon name="mobile alternate" />
                Relance SMS
              </Button>
            ) : null}
            {canRequestCheck ? (
              <Button
                basic
                color="grey"
                onClick={async (): Promise<void> => openCheckModal(review)}
                disabled={isLockedOrArchived}
              >
                <Icon name="clipboard check" />
                Vérifier
              </Button>
            ) : null}
            {canToggleSpam ? (
              <SpamReviewButton
                review={review}
                onReviewChange={onReviewChange}
                disabled={isLockedOrArchived}
              />
            ) : null}
            {canEdit ? (
              <EditReviewButton
                reviewId={review.id}
                page={page || 1}
                disabled={isLockedOrArchived}
              />
            ) : null}
            {canManageProof ? (
              <ProofDocumentButton
                reviewId={review.id}
                proofStatus={
                  isProofStatus(review.proofStatus)
                    ? review.proofStatus
                    : undefined
                }
                onProofChange={onReviewChange}
                disabled={isLockedOrArchived}
              />
            ) : null}
            {isPublishedOnline ? (
              <>
                <ViewReviewOnlineButton review={review} />
                <CopyReviewLinkButton review={review} />
              </>
            ) : null}
            <ReviewPresentationsButton
              review={review}
              onReviewChange={onReviewChange}
            />
          </span>
        }
      />
      {canToggleLock ? (
        <ToggleLockReviewButton
          reviewId={review.id}
          disabled={isLockedOrArchived}
          onComplete={() => {
            onReviewChange();
          }}
        />
      ) : null}
      <ReviewActionsModal
        closeModal={closeModal}
        modalMetadata={modalMetadata}
        onReviewChange={onReviewChange}
      />
    </>
  );
};
